import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Plus, Pencil, Trash2, UserPlus, Shield, User, X, Ruler, Scale, Target, Mail, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const POSITIONS = [
  { id: 'pg', name: 'Point Guard (1)' },
  { id: 'sg', name: 'Shooting Guard (2)' },
  { id: 'sf', name: 'Small Forward (3)' },
  { id: 'pf', name: 'Power Forward (4)' },
  { id: 'c', name: 'Center (5)' },
];

export default function AdminUsers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [roles, setRoles] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    display_name: '',
    role: 'member'
  });

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [user]);

  const loadUsers = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/users?admin_id=${user.uid}`);
      const data = await response.json();
      
      if (data.ok) {
        setUsers(data.users);
      } else {
        setError(data.detail || 'Fehler beim Laden der Benutzer');
      }
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/roles`);
      const data = await response.json();
      if (data.ok) {
        setRoles(data.roles);
      }
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users?admin_id=${user.uid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.ok) {
        alert(`✅ ${data.message}`);
        setShowCreateModal(false);
        setFormData({ email: '', password: '', display_name: '', role: 'member' });
        loadUsers();
      } else {
        alert(`❌ ${data.detail || 'Fehler'}`);
      }
    } catch (err) {
      alert('❌ Fehler beim Erstellen');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    // Build the new email from login_name
    const newEmail = `${formData.login_name}@rhein-stars.app`;
    const oldLoginName = editingUser.email?.split('@')[0] || '';
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${editingUser.id}?admin_id=${user.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: formData.display_name,
          email: formData.login_name !== oldLoginName ? newEmail : undefined,
          contact_email: formData.contact_email || null,
          new_password: formData.new_password || null,
          role: formData.role,
          disabled: formData.disabled,
          // Player data
          height: formData.height ? parseInt(formData.height) : null,
          weight: formData.weight ? parseInt(formData.weight) : null,
          position: formData.position || null
        })
      });
      
      const data = await response.json();
      
      if (data.ok) {
        alert('✅ Benutzer aktualisiert' + (formData.new_password ? ' (inkl. neues Passwort)' : ''));
        setEditingUser(null);
        loadUsers();
      } else {
        alert(`❌ ${data.detail || 'Fehler'}`);
      }
    } catch (err) {
      alert('❌ Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`⚠️ Benutzer "${userName}" wirklich löschen?\n\nDies kann nicht rückgängig gemacht werden!`)) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}?admin_id=${user.uid}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.ok) {
        alert(`✅ ${data.message}`);
        loadUsers();
      } else {
        alert(`❌ ${data.detail || 'Fehler'}`);
      }
    } catch (err) {
      alert('❌ Fehler beim Löschen');
    }
  };

  const openEditModal = (userToEdit) => {
    setEditingUser(userToEdit);
    // Extract login name from email
    const loginName = userToEdit.email?.split('@')[0] || '';
    setFormData({
      display_name: userToEdit.display_name,
      login_name: loginName,
      contact_email: userToEdit.contact_email || '',
      new_password: '',
      role: userToEdit.role,
      disabled: userToEdit.disabled || false,
      // Player data
      height: userToEdit.height || '',
      weight: userToEdit.weight || '',
      position: userToEdit.position || ''
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4 text-red-400" />;
      case 'moderator': return <Shield className="w-4 h-4 text-yellow-400" />;
      default: return <User className="w-4 h-4 text-gray-400" />;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-600/30 text-red-300',
      moderator: 'bg-yellow-600/30 text-yellow-300',
      member: 'bg-gray-600/30 text-gray-300'
    };
    return colors[role] || colors.member;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0409] text-rose-100 p-4 flex items-center justify-center">
        Laden...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0d0409] text-rose-100 p-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 mb-4 text-rose-200/70">
            <ArrowLeft className="w-5 h-5" /> Zurück
          </button>
          <Card className="bg-red-900/20 border-red-500/50">
            <CardContent className="pt-6 text-center">
              <p className="text-red-400">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0409] text-rose-100 p-4 pb-20">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-rose-200/70">
            <ArrowLeft className="w-5 h-5" /> Zurück
          </button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-green-700 hover:bg-green-800"
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Neuer Benutzer
          </Button>
        </div>

        <h1 className="text-2xl font-bold mb-4">👥 Benutzerverwaltung</h1>
        <p className="text-rose-200/60 mb-4">{users.length} Benutzer</p>

        {/* User List */}
        <div className="space-y-2">
          {users.map(u => {
            // Extract login name from email (part before @)
            const loginName = u.email?.split('@')[0] || '';
            
            return (
              <div 
                key={u.id} 
                className={`bg-[#1a0d11] border rounded-lg p-3 flex items-center justify-between ${
                  u.disabled ? 'border-red-500/30 opacity-60' : 'border-white/10'
                }`}
                data-testid={`user-row-${u.id}`}
              >
                <div className="flex items-center gap-3">
                  {getRoleIcon(u.role)}
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {u.display_name}
                      {u.disabled && <span className="text-xs text-red-400">(deaktiviert)</span>}
                    </div>
                    <div className="text-sm text-rose-200/50 flex items-center gap-2">
                      <span className="bg-blue-600/20 text-blue-300 px-1.5 py-0.5 rounded text-xs">
                        {loginName}
                      </span>
                      <span className="text-rose-200/30">•</span>
                      <span>{u.email}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs ${getRoleBadge(u.role)}`}>
                    {u.role}
                  </span>
                  <button 
                    onClick={() => openEditModal(u)}
                    className="p-2 hover:bg-white/10 rounded"
                    data-testid={`edit-user-${u.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleDelete(u.id, u.display_name)}
                    className="p-2 hover:bg-red-900/30 rounded text-red-400"
                    data-testid={`delete-user-${u.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <Card className="bg-[#1a0d11] border-white/20 w-full max-w-md">
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Neuer Benutzer</h2>
                  <button onClick={() => setShowCreateModal(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <label className="block text-sm text-rose-200/60 mb-1">Name</label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={e => setFormData({...formData, display_name: e.target.value})}
                      className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-rose-200/60 mb-1">E-Mail</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-rose-200/60 mb-1">Passwort</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={e => setFormData({...formData, password: e.target.value})}
                      className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                      required
                      minLength={6}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-rose-200/60 mb-1">Rolle</label>
                    <select
                      value={formData.role}
                      onChange={e => setFormData({...formData, role: e.target.value})}
                      className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                    >
                      {roles.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                      Abbrechen
                    </Button>
                    <Button type="submit" className="flex-1 bg-green-700 hover:bg-green-800">
                      Erstellen
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <Card className="bg-[#1a0d11] border-white/20 w-full max-w-md my-4">
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Benutzer bearbeiten</h2>
                  <button onClick={() => setEditingUser(null)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <form onSubmit={handleUpdate} className="space-y-4">
                  <div>
                    <label className="block text-sm text-rose-200/60 mb-1">Anzeigename</label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={e => setFormData({...formData, display_name: e.target.value})}
                      className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                      required
                      data-testid="edit-user-name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-rose-200/60 mb-1">Login-Name (Anmeldung)</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={formData.login_name}
                        onChange={e => setFormData({...formData, login_name: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '')})}
                        className="flex-1 bg-[#0d0409] border border-white/20 rounded-l px-3 py-2"
                        required
                        data-testid="edit-user-login"
                        placeholder="z.B. daniell"
                      />
                      <span className="bg-[#1a0d11] border border-white/10 border-l-0 rounded-r px-3 py-2 text-rose-200/50">
                        @rhein-stars.app
                      </span>
                    </div>
                    {formData.login_name !== editingUser.email?.split('@')[0] && (
                      <p className="text-xs text-yellow-400 mt-1">
                        ⚠️ Login wird geändert von "{editingUser.email?.split('@')[0]}" zu "{formData.login_name}"
                      </p>
                    )}
                  </div>
                  
                  {/* Password */}
                  <div>
                    <label className="flex items-center gap-1 text-sm text-rose-200/60 mb-1">
                      <Lock className="w-3 h-3" /> Neues Passwort setzen
                    </label>
                    <input
                      type="password"
                      value={formData.new_password}
                      onChange={e => setFormData({...formData, new_password: e.target.value})}
                      className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                      placeholder="Leer lassen = nicht ändern"
                      minLength={6}
                      data-testid="edit-user-password"
                    />
                    {formData.new_password && formData.new_password.length < 6 && (
                      <p className="text-xs text-red-400 mt-1">Mindestens 6 Zeichen</p>
                    )}
                  </div>
                  
                  {/* Contact Email */}
                  <div>
                    <label className="flex items-center gap-1 text-sm text-rose-200/60 mb-1">
                      <Mail className="w-3 h-3" /> E-Mail Adresse (Kontakt)
                    </label>
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={e => setFormData({...formData, contact_email: e.target.value})}
                      className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                      placeholder="spieler@gmail.com"
                      data-testid="edit-user-contact-email"
                    />
                    <p className="text-xs text-rose-200/40 mt-1">
                      Persönliche E-Mail für Benachrichtigungen
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-rose-200/60 mb-1">Rolle</label>
                    <select
                      value={formData.role}
                      onChange={e => setFormData({...formData, role: e.target.value})}
                      className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                      data-testid="edit-user-role"
                    >
                      {roles.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Player Data Section */}
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-orange-300 mb-3 flex items-center gap-2">
                      🏀 Spielerdaten
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="flex items-center gap-1 text-xs text-rose-200/60 mb-1">
                          <Ruler className="w-3 h-3" /> Größe (cm)
                        </label>
                        <input
                          type="number"
                          value={formData.height}
                          onChange={e => setFormData({...formData, height: e.target.value})}
                          className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                          placeholder="185"
                          min="140"
                          max="230"
                          data-testid="edit-user-height"
                        />
                      </div>
                      
                      <div>
                        <label className="flex items-center gap-1 text-xs text-rose-200/60 mb-1">
                          <Scale className="w-3 h-3" /> Gewicht (kg)
                        </label>
                        <input
                          type="number"
                          value={formData.weight}
                          onChange={e => setFormData({...formData, weight: e.target.value})}
                          className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                          placeholder="80"
                          min="40"
                          max="200"
                          data-testid="edit-user-weight"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <label className="flex items-center gap-1 text-xs text-rose-200/60 mb-1">
                        <Target className="w-3 h-3" /> Position
                      </label>
                      <select
                        value={formData.position}
                        onChange={e => setFormData({...formData, position: e.target.value})}
                        className="w-full bg-[#0d0409] border border-white/20 rounded px-3 py-2"
                        data-testid="edit-user-position"
                      >
                        <option value="">Keine Position</option>
                        {POSITIONS.map(pos => (
                          <option key={pos.id} value={pos.id}>{pos.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="disabled"
                      checked={formData.disabled || false}
                      onChange={e => setFormData({...formData, disabled: e.target.checked})}
                      className="rounded"
                      data-testid="edit-user-disabled"
                    />
                    <label htmlFor="disabled" className="text-sm text-rose-200/60">
                      Benutzer deaktivieren (kann sich nicht mehr anmelden)
                    </label>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setEditingUser(null)} className="flex-1">
                      Abbrechen
                    </Button>
                    <Button type="submit" className="flex-1 bg-blue-700 hover:bg-blue-800" data-testid="save-user-btn">
                      Speichern
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
